import React, { useCallback } from "react";
import PropTypes from "prop-types";

import { Auth } from "aws-amplify";
import { Button, Header, Placeholder } from "semantic-ui-react";

import { FeatureFlag } from "components/FeatureFlag";

import {
  ModalTypes,
  useCancellationSurvey,
  useGetPracticeSubscription,
  useModalLauncherFromSearchParam,
  useSubscribeToChangePlan,
  useSubscribeToSubscriptionChanged,
} from "./hooks";
import {
  LegacyCancelSubscription,
  StaticBulletList,
  SubscriptionBillingInfo,
  SubscriptionCancellation,
  SubscriptionOutdated,
  SubscriptionScheduledChanges,
  SubscriptionSummary,
} from "./components";

function SubscriptionPlaceholder() {
  return (
    <Placeholder data-test="subscription-placeholder">
      <Placeholder.Paragraph>
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder.Paragraph>

      <Placeholder.Paragraph>
        <Placeholder.Line />
        <Placeholder.Line length="medium" />
        <Placeholder.Line length="medium" />
        <Placeholder.Line length="medium" />
      </Placeholder.Paragraph>

      <Placeholder.Paragraph>
        <Placeholder.Line />
      </Placeholder.Paragraph>
    </Placeholder>
  );
}

function Subscription({ practiceId, user }) {
  const openSurvey = useCancellationSurvey(user);
  const {
    subscription,
    error,
    loading,
    reloadData,
    isAccountOwner,
    isApiPartnerPlan,
    disableChangePlanButton,
  } = useGetPracticeSubscription(practiceId);
  const { openModal } = useModalLauncherFromSearchParam();

  useSubscribeToChangePlan({
    onPlanChanged: useCallback(reloadData, [reloadData]),
  });

  useSubscribeToSubscriptionChanged({
    onSubscriptionChanged: useCallback(reloadData, [reloadData]),
  });

  const isSubscriptionRefundable = subscription?.refundable;

  const handleSubscriptionCancelled = useCallback(
    (_, { userRequestedRefund }) => {
      if (isSubscriptionRefundable && userRequestedRefund) {
        openSurvey();
        Auth.signOut();
      } else {
        reloadData();
        openSurvey();
      }
    },
    [reloadData, openSurvey, isSubscriptionRefundable]
  );

  const getContent = () => {
    if (loading) {
      return <SubscriptionPlaceholder />;
    }

    if (error) {
      return <span>{error}</span>;
    }

    if (!subscription) return <LegacyCancelSubscription />;

    if (!subscription.upToDate)
      return (
        <React.Fragment>
          <SubscriptionOutdated />
          <SubscriptionCancellation
            subscription={subscription}
            onCancelled={handleSubscriptionCancelled}
          />
        </React.Fragment>
      );

    return (
      <React.Fragment>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <SubscriptionSummary
            status={subscription.status}
            name={subscription?.plan?.name}
            monthlyPrice={subscription.planOption.pricing.monthlyPrice}
            billingPeriod={subscription.planOption.pricing.billingPeriod}
            currency={subscription.planOption.pricing.currency}
          />

          <SubscriptionScheduledChanges
            scheduledChanges={subscription.scheduledChanges}
            scheduledChangesAt={subscription.scheduledChangesAt}
          />

          <SubscriptionBillingInfo
            status={subscription.status}
            currency={subscription.planOption.pricing.currency}
            nextBillingPrice={subscription?.nextBillingPrice}
            billingPeriod={subscription.planOption.pricing.billingPeriod}
            nextBillingDate={subscription?.nextBillingDate}
            trialEndDate={subscription?.trialEndDate}
            cancellationDate={subscription?.cancellationDate}
          />
        </div>

        {subscription?.plan?.featureList && (
          <strong style={{ fontSize: "15px" }}>
            What&apos;s included in your subscription:
          </strong>
        )}

        <StaticBulletList items={subscription?.plan?.featureList} />

        <div style={{ display: "flex", gap: "10px" }}>
          <FeatureFlag id="change-plan-button-in-account-settings">
            {isAccountOwner && !isApiPartnerPlan && (
              <Button
                primary
                data-dd-action-name="Change Plan from Account Settings"
                disabled={disableChangePlanButton}
                content="Change Plan"
                style={{ alignSelf: "flex-start" }}
                onClick={() => {
                  openModal(ModalTypes.CHANGE_PLAN);
                }}
              />
            )}
          </FeatureFlag>

          {isAccountOwner && !isApiPartnerPlan && (
            <SubscriptionCancellation
              subscription={subscription}
              onCancelled={handleSubscriptionCancelled}
            />
          )}
        </div>
      </React.Fragment>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <Header as="h1" style={{ margin: "0" }}>
        Subscription
      </Header>

      {getContent()}
    </div>
  );
}

Subscription.propTypes = {
  practiceId: PropTypes.string.isRequired,
  user: PropTypes.object,
};

Subscription.defaultProps = { user: undefined };

export default Subscription;
